import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Globe, ArrowLeft, FileCheck, CheckCircle2, 
  Target, TrendingUp, Users, Lightbulb, MapPin,
  ChevronRight, RefreshCw, Sparkles, MessageCircle
} from 'lucide-react';

const CRITERIA_ICONS = {
  innovation: Lightbulb,
  viability: Target,
  scalability: TrendingUp,
  market_knowledge: Users,
  uk_benefit: MapPin
};

const CRITERIA_COLORS = {
  innovation: 'from-amber-600 to-amber-800',
  viability: 'from-blue-600 to-blue-800',
  scalability: 'from-purple-600 to-purple-800',
  market_knowledge: 'from-emerald-600 to-emerald-800',
  uk_benefit: 'from-red-600 to-red-800'
};

const SECTION_NAMES = {
  executive_summary: 'Executive Summary',
  problem: 'Problem Statement',
  solution: 'Solution',
  market: 'Market Analysis',
  team: 'Team',
  financials: 'Financial Projections'
};

function EndorsementReport() {
  const auth = useAuth();
  const token = auth.token;
  
  const [planContent, setPlanContent] = useState({
    executive_summary: '',
    problem: '',
    solution: '',
    market: '',
    team: '',
    financials: ''
  });
  const [loading, setLoading] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [criteria, setCriteria] = useState(null);
  const [activeSection, setActiveSection] = useState('executive_summary');

  async function handleScorePlan() {
    const hasContent = Object.values(planContent).some(v => v.trim().length > 0);
    if (!hasContent) {
      toast.error('Please add content to at least one section');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(API + '/ai/endorsement/score', {
        plan_content: planContent
      }, {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      setAssessment(response.data.assessment);
      setCriteria(response.data.criteria);
      toast.success('Assessment complete!');
    } catch (error) {
      console.error('Failed to score plan:', error);
      toast.error('Failed to score plan. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleSectionChange(section) {
    setActiveSection(section);
  }

  function handleContentChange(e) {
    setPlanContent(prev => ({
      ...prev,
      [activeSection]: e.target.value
    }));
  }

  function countFilledSections() {
    let count = 0;
    const keys = Object.keys(planContent);
    for (let i = 0; i < keys.length; i++) {
      if (planContent[keys[i]].trim().length > 50) {
        count++;
      }
    }
    return count;
  }

  const filledCount = countFilledSections();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="endorsement-page">
      {/* Ambient Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#DC143C] opacity-5 blur-[150px] rounded-full" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#8B0000] opacity-5 blur-[150px] rounded-full" />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-header">
        <div className="container-wide py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-[#71717A] hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <Link to="/" className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl gradient-red flex items-center justify-center">
                  <Globe className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold tracking-tight hidden sm:block">
                  <span className="text-white">Visa</span>
                  <span className="text-gradient-red">Founders</span>
                </span>
              </Link>
            </div>
            <Badge className="badge-red">
              <Sparkles className="w-3 h-3 mr-1" />
              GPT-5.2 Powered
            </Badge>
          </div>
        </div>
      </nav>

      <main className="container-wide pt-28 pb-12 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Endorsement <span className="text-gradient-red">Readiness</span> Engine
          </h1>
          <p className="text-[#A1A1AA] text-lg">
            Score your business plan against official UK Home Office criteria
          </p>
        </motion.div>

        {/* Criteria Overview */}
        <CriteriaOverview />
        
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div>
            <div className="card-dark rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Your Business Plan</h2>
                <Badge className="badge-dark">
                  {filledCount}/6 Sections
                </Badge>
              </div>
              
              <p className="text-[#A1A1AA] text-sm mb-6">
                Paste your business plan content section by section. The AI will score each section against the UK Innovator Founder Visa endorsement criteria.
              </p>
              
              <SectionTabs 
                sections={Object.keys(SECTION_NAMES)}
                activeSection={activeSection}
                planContent={planContent}
                onChange={handleSectionChange}
              />
              
              <div className="mt-4 mb-6">
                <label className="block text-sm font-medium mb-2 text-[#A1A1AA]">
                  {SECTION_NAMES[activeSection]}
                </label>
                <Textarea
                  value={planContent[activeSection]}
                  onChange={handleContentChange}
                  placeholder={'Enter your ' + SECTION_NAMES[activeSection].toLowerCase() + ' content here...'}
                  className="input-dark min-h-64"
                  data-testid="endorsement-content-input"
                />
              </div>
              
              <Button
                onClick={handleScorePlan}
                disabled={loading || filledCount === 0}
                className="w-full btn-primary py-6 text-lg glow-red"
                data-testid="endorsement-score-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Analyzing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <FileCheck className="w-5 h-5" />
                    Score Against Criteria
                  </span>
                )}
              </Button>
            </div>
          </div>
          
          {/* Assessment Results */}
          <div>
            {assessment ? (
              <AssessmentResults 
                assessment={assessment}
                criteria={criteria}
                onReset={() => {
                  setAssessment(null);
                  setCriteria(null);
                }}
              />
            ) : (
              <div className="card-dark rounded-2xl p-8 h-full flex flex-col items-center justify-center text-center">
                <div className="w-20 h-20 rounded-2xl bg-white/5 flex items-center justify-center mb-6">
                  <FileCheck className="w-10 h-10 text-[#71717A]" />
                </div>
                <h3 className="text-xl font-bold mb-2">No Assessment Yet</h3>
                <p className="text-[#71717A] max-w-sm">
                  Add your business plan content and click &quot;Score&quot; to get an AI-powered assessment against the UK Innovator Founder Visa endorsement criteria.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Support Button */}
      <button className="support-btn" data-testid="support-btn">
        <MessageCircle className="w-6 h-6 text-white" />
      </button>
    </div>
  );
}

function CriteriaOverview() {
  const criteriaItems = [
    { key: 'innovation', name: 'Innovation', weight: 25, icon: Lightbulb },
    { key: 'viability', name: 'Viability', weight: 20, icon: Target },
    { key: 'scalability', name: 'Scalability', weight: 25, icon: TrendingUp },
    { key: 'market_knowledge', name: 'Market Knowledge', weight: 15, icon: Users },
    { key: 'uk_benefit', name: 'UK Benefit', weight: 15, icon: MapPin }
  ];
  
  const cards = [];
  for (let i = 0; i < criteriaItems.length; i++) {
    const item = criteriaItems[i];
    const IconComponent = item.icon;
    cards.push(
      <motion.div 
        key={item.key}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: i * 0.1 }}
        className="text-center p-4"
      >
        <div className={'w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-3 bg-gradient-to-br ' + CRITERIA_COLORS[item.key]}>
          <IconComponent className="w-7 h-7 text-white" />
        </div>
        <h3 className="font-bold text-sm mb-1">{item.name}</h3>
        <Badge className="badge-dark text-xs">{item.weight} pts</Badge>
      </motion.div>
    );
  }
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-glass rounded-2xl p-6 mb-8"
    >
      <h2 className="text-xl font-bold mb-2">UK Innovator Founder Visa Criteria</h2>
      <p className="text-[#A1A1AA] text-sm mb-6">
        Your business plan will be scored against these 5 official endorsement criteria
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {cards}
      </div>
    </motion.div>
  );
}

function SectionTabs({ sections, activeSection, planContent, onChange }) {
  const tabs = [];
  
  for (let i = 0; i < sections.length; i++) {
    const key = sections[i];
    const name = SECTION_NAMES[key];
    const hasContent = planContent[key].trim().length > 50;
    const isActive = activeSection === key;
    
    tabs.push(
      <button
        key={key}
        onClick={() => onChange(key)}
        className={'px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ' +
          (isActive ? 'bg-[#DC143C] text-white' : 
           hasContent ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 
           'bg-white/5 text-[#A1A1AA] hover:bg-white/10')}
        data-testid={'endorsement-tab-' + key}
      >
        {name}
        {hasContent && !isActive && (
          <CheckCircle2 className="w-3 h-3 inline ml-1" />
        )}
      </button>
    );
  }
  
  return (
    <div className="flex flex-wrap gap-2">
      {tabs}
    </div>
  );
}

function AssessmentResults({ assessment, criteria, onReset }) {
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="card-featured rounded-2xl overflow-hidden"
      data-testid="endorsement-assessment"
    >
      <div className="gradient-red px-6 py-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
            <FileCheck className="w-7 h-7 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Assessment Complete</h2>
            <p className="text-white/70 text-sm">AI-powered analysis against UK Home Office criteria</p>
          </div>
        </div>
      </div>
      
      {criteria && <CriteriaBreakdown criteria={criteria} />}
      
      <div className="p-6">
        <h3 className="font-bold mb-4 text-[#A1A1AA] text-sm uppercase tracking-wider">Detailed Assessment</h3>
        <div className="prose prose-invert max-w-none">
          <div className="whitespace-pre-wrap text-[#A1A1AA] text-sm leading-relaxed">
            {assessment}
          </div>
        </div>
      </div>
      
      <div className="px-6 pb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            onClick={onReset}
            className="btn-secondary flex-1"
            data-testid="endorsement-new-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            New Assessment
          </Button>
          <Link to="/tools/vc-interview" className="flex-1">
            <Button className="w-full btn-primary glow-red" data-testid="endorsement-go-to-vc-btn">
              Test with VC Interview
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

function CriteriaBreakdown({ criteria }) {
  if (!criteria) return null;
  
  const cards = [];
  const keys = Object.keys(criteria);
  
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const item = criteria[key];
    const IconComponent = CRITERIA_ICONS[key] || Target;
    const colorClass = CRITERIA_COLORS[key] || 'from-gray-600 to-gray-800';
    
    cards.push(
      <div key={key} className="card-dark rounded-lg p-3" data-testid={'criteria-' + key}>
        <div className="flex items-center gap-3">
          <div className={'w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br ' + colorClass}>
            <IconComponent className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-semibold text-sm truncate">{item.name}</h4>
              <Badge className="badge-red text-xs">{item.weight}pts</Badge>
            </div>
            <p className="text-xs text-[#71717A] truncate">{item.description}</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6 border-b border-white/10">
      <h3 className="font-bold mb-4 text-[#A1A1AA] text-sm uppercase tracking-wider">Criteria Breakdown</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cards}
      </div>
    </div>
  );
}

export default EndorsementReport;
